import * as React from "react"
import { Mail, X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useAuth } from "@/lib/auth"
import { emailConfirmationRedirectUrl, supabase } from "@/lib/supabase"
import { icon, statusBg, statusText } from "@/lib/design-tokens"
import { cn } from "@/lib/utils"

type ResendState = "idle" | "loading" | "success" | "error"

export function EmailVerificationBanner() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [dismissed, setDismissed] = React.useState(false)
  const [resendState, setResendState] = React.useState<ResendState>("idle")

  const isUnverified = user && !user.email_confirmed_at
  if (!isUnverified || dismissed) return null

  async function handleResend() {
    if (!user?.email || resendState === "loading") return
    setResendState("loading")
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: user.email,
        options: { emailRedirectTo: emailConfirmationRedirectUrl() },
      })
      if (error) throw error
      setResendState("success")
      setTimeout(() => setResendState("idle"), 3000)
    } catch {
      setResendState("error")
      setTimeout(() => setResendState("idle"), 4000)
    }
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex shrink-0 items-start justify-between gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/40 dark:text-amber-200 sm:items-center"
    >
      {/* Left: icon + text + button */}
      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
        <div className="flex items-center gap-2">
          <Mail className={cn(icon.md, "shrink-0 opacity-80")} aria-hidden />
          <span className="text-sm font-medium">{t("verifyBanner.message")}</span>
        </div>
        <span className="hidden text-sm opacity-70 sm:inline">—</span>
        <span className="text-xs text-amber-800 opacity-80 dark:text-amber-300 sm:text-sm">
          {t("verifyBanner.description")}{" "}
          <span className="font-medium">{user.email}</span>
        </span>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void handleResend()}
          disabled={resendState === "loading" || resendState === "success"}
          className={cn(
            "mt-1 h-auto w-fit px-2.5 py-1 text-xs font-medium sm:mt-0",
            resendState === "success"
              ? cn(statusBg.ok, statusText.ok)
              : resendState === "error"
                ? cn(statusBg.danger, statusText.danger)
                : cn(statusBg.warning, statusText.warning, "hover:bg-muted/70 disabled:opacity-60")
          )}
        >
          {resendState === "loading"
            ? t("verifyBanner.resending")
            : resendState === "success"
              ? t("verifyBanner.resent")
              : resendState === "error"
                ? t("verifyBanner.error")
                : t("verifyBanner.resend")}
        </Button>
      </div>

      {/* Right: dismiss */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => setDismissed(true)}
        aria-label={t("verifyBanner.dismiss")}
        className="h-8 w-8 shrink-0 opacity-60 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-amber-500"
      >
        <X className={cn(icon.md)} aria-hidden />
      </Button>
    </div>
  )
}
